import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from 'react';

const MapContext = createContext(null);

export const MapProvider = ({ children }) => {
    const [map, setMapState] = useState(null);
    const [countryList, setCountryList] = useState({
        countries: [],
        isDropdownOpen: false,
        setIsDropdownOpen: () => {},
        activeCountryCode: null,
        onCountryClick: () => {},
    });

    const addLayer = useCallback(
        (layerConfig) => {
            if (!map) return;

            const { id, type, source, paint, layout, filter } = layerConfig;

            if (map.getLayer(id)) {
                map.removeLayer(id);
            }

            map.addLayer({ id, type, source, paint, layout, filter });
        },
        [map]
    );

    const addSource = useCallback(
        (sourceConfig) => {
            if (!map) return;

            const { id, type, data } = sourceConfig;

            if (map.getSource(id)) {
                map.removeSource(id);
            }

            map.addSource(id, { type, data });
        },
        [map]
    );

    const removeLayer = useCallback(
        (layerId) => {
            if (!map) return;
            if (map.getLayer(layerId)) {
                map.removeLayer(layerId);
            }
        },
        [map]
    );

    const removeSource = useCallback(
        (sourceId) => {
            if (!map) return;
            if (map.getSource(sourceId)) {
                map.removeSource(sourceId);
            }
        },
        [map]
    );

    const setMap = useCallback((newMap) => {
        setMapState(newMap);
    }, []);

    const contextValue = useMemo(
        () => ({
            map,
            setMap,
            addLayer,
            addSource,
            removeLayer,
            removeSource,
            countryList,
            setCountryList,
        }),
        [
            map,
            setMap,
            addLayer,
            addSource,
            removeLayer,
            removeSource,
            countryList,
        ]
    );

    return (
        <MapContext.Provider value={contextValue}>
            {children}
        </MapContext.Provider>
    );
};

export const useMap = () => {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error('useMap must be used within a MapProvider');
    }
    return context;
};
